import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import canadaFlag from '../../../../images/canadaflag.png';

// ─────────────────────────────────────────────────────────
// Animated counter — interpolates between values smoothly
// ─────────────────────────────────────────────────────────
function AnimatedNumber({ value, duration = 380, format = (v) => v.toLocaleString() }) {
    const [display, setDisplay] = useState(value);
    const fromRef = useRef(value);
    const startRef = useRef(0);
    const rafRef = useRef(0);

    useEffect(() => {
        cancelAnimationFrame(rafRef.current);
        fromRef.current = display;
        startRef.current = performance.now();
        const tick = (now) => {
            const elapsed = now - startRef.current;
            const t = Math.min(1, elapsed / duration);
            // easeOutCubic
            const eased = 1 - Math.pow(1 - t, 3);
            const next = fromRef.current + (value - fromRef.current) * eased;
            setDisplay(next);
            if (t < 1) {
                rafRef.current = requestAnimationFrame(tick);
            } else {
                setDisplay(value);
            }
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, duration]);

    return <>{format(Math.round(display))}</>;
}

// ─────────────────────────────────────────────────────────
// Pricing calculator — interactive cost comparison
// ─────────────────────────────────────────────────────────
function PricingCalculator() {
    const [users, setUsers] = useState(40);
    const [projects, setProjects] = useState(2);

    // Pricing model
    // By User plan: one user license = unlimited project access
    const byUserMonthly = users * 39;

    // By Project plan: per-project flat $599, with 50 active users included PER project.
    // Assume users distributed evenly across projects (worst-case integer rounding).
    const usersPerProject = Math.ceil(users / projects);
    const overagePerProject = Math.max(0, usersPerProject - 50) * 39;
    const byProjectMonthly = projects * (599 + overagePerProject);

    const userCheaper = byUserMonthly <= byProjectMonthly;
    const savings = Math.abs(byUserMonthly - byProjectMonthly);
    const recommendedMonthly = Math.min(byUserMonthly, byProjectMonthly);

    // Procore approximation — industry-cited per-user equivalent ~$105/mo
    const procoreEstimate = users * 105;
    const vsProcoreAnnual = Math.max(0, (procoreEstimate - recommendedMonthly) * 12);

    const userPct = ((users - 1) / (200 - 1)) * 100;
    const projectPct = ((projects - 1) / (10 - 1)) * 100;

    return (
        <div className="pl-pricing-calc">
            <div className="pl-pricing-calc__lead">
                <p className="pl-pricing-calc__eyebrow">Live calculator</p>
                <h3>Built to scale with any company, any project size.</h3>
                <p>
                    The <strong>By User</strong> plan covers <strong>unlimited projects</strong> with one
                    flat $39/user/mo seat. <strong>By Project</strong> caps a single project at $599/mo,
                    no matter the job&apos;s value. We built ProjectLoop <strong>lean from the ground up
                    — no bloat, no enterprise tax, no percentage cuts</strong>. Those savings go straight
                    to you. Drag the sliders — the right plan reveals itself.
                </p>
            </div>

            {/* Sliders side-by-side */}
            <div className="pl-calc-sliders">
                <div className="pl-calc-slider">
                    <div className="pl-calc-slider__head">
                        <span className="pl-calc-slider__label">Active projects</span>
                        <span className="pl-calc-slider__count">
                            <AnimatedNumber value={projects} duration={220} />
                            <small> {projects === 1 ? 'project' : 'projects'}</small>
                        </span>
                    </div>
                    <div className="pl-calc-slider__track">
                        <div className="pl-calc-slider__fill" style={{ width: `${projectPct}%` }} />
                        <input
                            type="range"
                            min={1}
                            max={10}
                            step={1}
                            value={projects}
                            onChange={(e) => setProjects(Number(e.target.value))}
                            className="pl-calc-slider__input"
                            aria-label="Number of active projects"
                        />
                    </div>
                    <div className="pl-calc-slider__ticks" aria-hidden="true">
                        <span>1</span>
                        <span>3</span>
                        <span>5</span>
                        <span>7</span>
                        <span>10</span>
                    </div>
                </div>

                <div className="pl-calc-slider">
                    <div className="pl-calc-slider__head">
                        <span className="pl-calc-slider__label">Total active users</span>
                        <span className="pl-calc-slider__count">
                            <AnimatedNumber value={users} duration={220} />
                            <small> {users === 1 ? 'user' : 'users'}</small>
                        </span>
                    </div>
                    <div className="pl-calc-slider__track">
                        <div className="pl-calc-slider__fill" style={{ width: `${userPct}%` }} />
                        <input
                            type="range"
                            min={1}
                            max={200}
                            step={1}
                            value={users}
                            onChange={(e) => setUsers(Number(e.target.value))}
                            className="pl-calc-slider__input"
                            aria-label="Number of total active users"
                        />
                    </div>
                    <div className="pl-calc-slider__ticks" aria-hidden="true">
                        <span>1</span>
                        <span>50</span>
                        <span>100</span>
                        <span>150</span>
                        <span>200</span>
                    </div>
                </div>
            </div>

            {/* Plan results */}
            <div className="pl-calc-results">
                <div className={`pl-calc-card${userCheaper ? ' is-best' : ''}`}>
                    {userCheaper && <span className="pl-calc-card__badge">Best fit · save ${savings.toLocaleString()}/mo</span>}
                    <span className="pl-calc-card__plan">By User · unlimited projects</span>
                    <span className="pl-calc-card__trait pl-calc-card__trait--user">
                        ↕ Variable · scale up or down monthly
                    </span>
                    <div className="pl-calc-card__price">
                        $<AnimatedNumber value={byUserMonthly} duration={300} />
                        <small>/mo</small>
                    </div>
                    <div className="pl-calc-card__detail">
                        {users} × $39/user/mo · works across <strong>any number</strong> of projects
                    </div>
                    <div className="pl-calc-card__annual">
                        $<AnimatedNumber value={byUserMonthly * 12} duration={300} /> per year
                    </div>
                </div>

                <div className={`pl-calc-card${!userCheaper ? ' is-best' : ''}`}>
                    {!userCheaper && <span className="pl-calc-card__badge">Best fit · save ${savings.toLocaleString()}/mo</span>}
                    <span className="pl-calc-card__plan">By Project · per project</span>
                    <span className="pl-calc-card__trait pl-calc-card__trait--project">
                        ◆ Flat · predictable budget locked in
                    </span>
                    <div className="pl-calc-card__price">
                        $<AnimatedNumber value={byProjectMonthly} duration={300} />
                        <small>/mo</small>
                    </div>
                    <div className="pl-calc-card__detail">
                        {projects} × $599 flat
                        {overagePerProject > 0 && ` + ${usersPerProject - 50} extra user${usersPerProject - 50 === 1 ? '' : 's'}/project × $39`}
                    </div>
                    <div className="pl-calc-card__annual">
                        $<AnimatedNumber value={byProjectMonthly * 12} duration={300} /> per year
                    </div>
                </div>
            </div>

            {/* Insight callout — why one plan beats the other for this scenario */}
            <div className="pl-calc-insight">
                <span className="pl-calc-insight__icon" aria-hidden="true">💡</span>
                <p>
                    {userCheaper ? (
                        <>
                            With <strong>{projects} project{projects === 1 ? '' : 's'}</strong> and
                            <strong> {users} user{users === 1 ? '' : 's'}</strong>, the
                            <strong> By User</strong> plan wins because <strong>one seat covers every
                            project you run</strong> — no per-project flat fees stacking up.
                        </>
                    ) : (
                        <>
                            With <strong>{projects} project{projects === 1 ? '' : 's'}</strong> averaging
                            <strong> {usersPerProject} user{usersPerProject === 1 ? '' : 's'} each</strong>,
                            the <strong>By Project</strong> plan wins —
                            {' '}<strong>$599 caps a busy project</strong> regardless of headcount or job
                            value.
                        </>
                    )}
                </p>
            </div>

            {/* Savings vs Procore callout */}
            <div className="pl-calc-vs-procore">
                <div className="pl-calc-vs-procore__bar">
                    <div className="pl-calc-vs-procore__row">
                        <span>Procore (typical)</span>
                        <strong>~${procoreEstimate.toLocaleString()}/mo</strong>
                    </div>
                    <div className="pl-calc-vs-procore__row pl-calc-vs-procore__row--us">
                        <span>ProjectLoop (best plan)</span>
                        <strong>${recommendedMonthly.toLocaleString()}/mo</strong>
                    </div>
                </div>
                <div className="pl-calc-vs-procore__pop">
                    You save approximately{' '}
                    <strong>
                        $<AnimatedNumber value={vsProcoreAnnual} duration={400} /> / year
                    </strong>
                    {' '}vs Procore.
                </div>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────
// "Why our pricing works" — value-prop cards
// ─────────────────────────────────────────────────────────
function WhichPlanWhen() {
    return (
        <div className="pl-pricing-when">
            <div className="pl-pricing-calc__lead">
                <p className="pl-pricing-calc__eyebrow pl-pricing-calc__eyebrow--alt">Which plan, when?</p>
                <h3>Two pricing models. Pick the one that matches how you run.</h3>
                <p>
                    Same platform, same features — different ways to pay so the model fits the
                    business, not the other way around.
                </p>
            </div>
            <div className="pl-pricing-when__grid">
                <div className="pl-when-card pl-when-card--user">
                    <span className="pl-when-card__chip">↕ Variable</span>
                    <h4>By User — flex with your team.</h4>
                    <p className="pl-when-card__sub">
                        $39/user/mo · scale up or down month-to-month
                    </p>
                    <ul>
                        <li>
                            <strong>Headcount changes month to month.</strong> Add seats when the season
                            ramps up, drop them when it slows down. You only pay for who&apos;s active.
                        </li>
                        <li>
                            <strong>You run multiple projects at once.</strong> One seat covers every
                            project — no per-project fees stacking up on the same team.
                        </li>
                        <li>
                            <strong>You&apos;re a small/mid GC growing fast.</strong> Variable rate moves
                            with you instead of locking you into a fixed bill before you&apos;re ready.
                        </li>
                    </ul>
                    <div className="pl-when-card__pull">
                        Best for crews that <strong>flex</strong>.
                    </div>
                </div>

                <div className="pl-when-card pl-when-card--project">
                    <span className="pl-when-card__chip">◆ Predictable</span>
                    <h4>By Project — flat, locked-in budgeting.</h4>
                    <p className="pl-when-card__sub">
                        $599/project/mo · 50 active users included · same price every month
                    </p>
                    <ul>
                        <li>
                            <strong>You need a fixed, predictable line item.</strong> Owners and CFOs
                            love it — budget the project once, no surprise software bills.
                        </li>
                        <li>
                            <strong>One busy jobsite with a big roster.</strong> 30, 40, 50 active users
                            on a single project? $599 caps it — no per-seat math.
                        </li>
                        <li>
                            <strong>You bill the project, not the team.</strong> Fold the flat fee
                            straight into your project&apos;s general conditions and forget about it.
                        </li>
                    </ul>
                    <div className="pl-when-card__pull">
                        Best for projects that <strong>need to lock in</strong>.
                    </div>
                </div>
            </div>
        </div>
    );
}

function WhyPricingWorks() {
    const reasons = [
        {
            stat: '$39',
            label: 'flat per active user',
            body: 'No tiered seat licenses, no role-based gotchas. Foreman, PM, estimator, owner — same price.',
        },
        {
            stat: '$599',
            label: 'caps any project',
            body: 'A $5M renovation. A $200M tower. Same monthly fee. Your software bill never balloons just because the job got bigger.',
        },
        {
            stat: 'Any',
            label: 'size of company',
            body: 'Two-person GC or 200-person enterprise — same platform, same flat rates. We scale with you, not against you.',
        },
        {
            stat: '0%',
            label: 'of your construction value',
            body: 'Your win is your win. Built lean from day one means no enterprise tax, no percentage cuts, no surprise renewal hikes.',
        },
    ];
    return (
        <div className="pl-pricing-why-pricing">
            <div className="pl-pricing-calc__lead">
                <p className="pl-pricing-calc__eyebrow pl-pricing-calc__eyebrow--alt">Why this pricing works</p>
                <h3>Lean from the ground up. Savings passed straight to you.</h3>
                <p>
                    Most construction PM tools charge you per role, per module, per percentage of project
                    value — they need to fund a sales floor, conferences, and middle management. We built
                    ProjectLoop <strong>without that bloat</strong> from day one, which means
                    <strong> we can support any size company on any size project</strong> for two simple
                    prices. Those savings? Yours.
                </p>
            </div>
            <div className="pl-pricing-why-pricing__grid">
                {reasons.map((r) => (
                    <div key={r.label} className="pl-pricing-why-pricing__card">
                        <span className="pl-pricing-why-pricing__stat">{r.stat}</span>
                        <span className="pl-pricing-why-pricing__label">{r.label}</span>
                        <p>{r.body}</p>
                    </div>
                ))}
            </div>
        </div>
    );
}

const coreFeatures = [
    'Drawings & version control',
    'RFIs',
    'Submittals',
    'Daily Logs',
    'Punch Lists',
    'Locations & Site QR',
    'Time Tracking',
    'Action Items',
    'Forms',
    'Documents',
    'Company Directory',
    'Mobile App (iOS + Android)',
];

const competitorRows = [
    {
        feature: 'How they bill you',
        projectloop: 'Flat $599 CAD / project / mo',
        procore: '1–3% of your Annual Construction Volume',
        plPositive: true,
        emphasis: true,
    },
    {
        feature: 'Cost when you win a bigger job',
        projectloop: 'Same flat rate. Always.',
        procore: 'Their bill goes up too — your win is theirs',
        plPositive: true,
        emphasis: true,
    },
    {
        feature: 'Annual minimum commitment',
        projectloop: 'No minimum — month-to-month',
        procore: '12-month minimum, paid annually',
        plPositive: true,
    },
    {
        feature: 'Pricing transparency',
        projectloop: 'Listed publicly. No sales call required.',
        procore: 'Custom quote. Sales call required.',
        plPositive: true,
    },
    {
        feature: 'Free trial',
        projectloop: '90-day free trial, no card, no commitment. 6 months free for SafetyLoop customers',
        procore: 'Demo only',
        plPositive: true,
    },
    {
        feature: 'Drawings + version control',
        projectloop: true,
        procore: true,
        plPositive: true,
    },
    {
        feature: 'RFIs · Submittals · Daily Logs · Punch',
        projectloop: true,
        procore: true,
        plPositive: true,
    },
    {
        feature: 'Forms & inspections',
        projectloop: true,
        procore: 'Add-on',
        plPositive: true,
    },
    {
        feature: 'Subcontractor accounts',
        projectloop: 'Unlimited & free to add on every plan',
        procore: 'Sub seats often billed at full rate',
        plPositive: true,
    },
    {
        feature: 'How sub users are billed',
        projectloop: 'Project: free up to 50 active users · User: $39/user/mo same as internal',
        procore: 'Per-seat fees often apply to sub users too',
        plPositive: true,
    },
    {
        feature: 'Project plan user cap',
        projectloop: '50 active users (internal + sub-connected) included · $39/user/mo above 50',
        procore: 'No flat per-project tier offered',
        plPositive: true,
    },
    {
        feature: 'Built for the Canadian market',
        projectloop: 'CAD pricing · Canadian compliance · local data residency',
        procore: 'US-headquartered · USD billing · US-first compliance',
        plPositive: true,
    },
    {
        feature: 'Typical small/mid GC annual cost',
        projectloop: '~$7k CAD all-in (one project plan)',
        procore: '$25k–$100k+ USD common',
        plPositive: true,
    },
];

export default function PricingPlans() {
    return (
        <div className="pricing-container">
            {/* ── Hero ───────────────────────────────────────────── */}
            <div className="pl-pricing-hero">
                <span className="pl-pricing-canada">
                    <img className="pl-pricing-canada__flag" src={canadaFlag} alt="Canadian flag" />
                    <span className="pl-pricing-canada__text">
                        <strong>Proudly Canadian.</strong>
                        <small>Built in Ontario · CAD pricing · Local data residency</small>
                    </span>
                </span>
                <h1>Pricing that finally makes sense.</h1>
                <p className="pl-pricing-lede">
                    Every plan unlocks the full platform. Public CAD pricing. No enterprise sales gauntlet.
                </p>
                <div className="pl-pricing-offers">
                    <div className="pl-pricing-offer pl-pricing-offer--trial">
                        <div className="pl-pricing-offer__seal">
                            <span>90</span>
                            <small>days free</small>
                        </div>
                        <div className="pl-pricing-offer__copy">
                            <strong>Three months on us.</strong>
                            <p>No credit card. No commitment. Cancel anytime.</p>
                        </div>
                        <Link to="/signup" className="pl-pricing-offer__cta-wrap">
                            <Button type="primary" className="pl-pricing-offer__cta">
                                Start free trial
                            </Button>
                        </Link>
                    </div>

                    {/* SafetyLoop loyalty special */}
                    <div className="pl-pricing-offer pl-pricing-offer--loyalty">
                        <div className="pl-pricing-offer__seal" aria-hidden="true">
                            <span>SL</span>
                            <small>+ PL</small>
                        </div>
                        <div className="pl-pricing-offer__copy">
                            <strong>
                                On SafetyLoop? <span>6 months FREE.</span>
                            </strong>
                            <p>Current SafetyLoop customers (Bronze or higher) try ProjectLoop free for 6 months instead of 90 days.</p>
                        </div>
                        <Link to="/signup?promo=SAFETYLOOPFREE" className="pl-pricing-offer__cta-wrap">
                            <Button type="primary" className="pl-pricing-offer__cta">
                                Claim 6 months free
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>

            {/* ── Plan tiers ─────────────────────────────────────── */}
            <div className="pl-pricing-plans">
                {/* FREE */}
                <div className="pl-plan">
                    <div className="pl-plan__head">
                        <span className="pl-plan__tier">Free Forever</span>
                        <h2>Free</h2>
                        <p className="pl-plan__desc">
                            Run a small project at no cost. Full feature access — limited seats and projects.
                        </p>
                        <div className="pl-plan__price">
                            <span className="pl-plan__price-num">$0</span>
                            <span className="pl-plan__price-suffix">CAD / month</span>
                        </div>
                    </div>
                    <Link to="/signup?plan=free">
                        <Button type="default" block size="large" className="pl-plan__cta pl-plan__cta--ghost">
                            Get started free
                        </Button>
                    </Link>
                    <p className="pl-plan__note">Up to 2 users · 1 active project</p>
                    <ul className="pl-plan__features">
                        {coreFeatures.map((f) => (
                            <li key={f}><CheckOutlined /> {f}</li>
                        ))}
                    </ul>
                </div>

                {/* BY PROJECT — featured (best value, flat fee) */}
                <div className="pl-plan pl-plan--featured pl-plan--hero">
                    <span className="pl-plan__ribbon">Best value · Flat fee</span>
                    <div className="pl-plan__head">
                        <span className="pl-plan__tier">Recommended</span>
                        <h2>By Project</h2>
                        <p className="pl-plan__desc">
                            <strong>One predictable bill per project.</strong> Add crews, swap subs, scale
                            the team — your software cost never moves. The smart choice for any active
                            jobsite.
                        </p>
                        <div className="pl-plan__price">
                            <span className="pl-plan__price-num">$599</span>
                            <span className="pl-plan__price-suffix">CAD / project / month</span>
                        </div>
                        <div className="pl-plan__price-meta">
                            <span className="pl-plan__price-saver">
                                <CheckOutlined /> Locked-in flat rate — no surprise overages
                            </span>
                            <span className="pl-plan__price-saver">
                                <CheckOutlined /> Pays for itself at ~16 users vs By User
                            </span>
                            <span className="pl-plan__price-saver">
                                <CheckOutlined /> Beats Procore by $65K–$740K+ per year
                            </span>
                        </div>
                    </div>
                    <Link to="/signup?plan=project">
                        <Button type="primary" block size="large" className="pl-plan__cta pl-plan__cta--prominent">
                            Start 90-day trial
                        </Button>
                    </Link>
                    <p className="pl-plan__note">Unlimited subcontractors · 50 active users included · flat monthly rate</p>
                    <ul className="pl-plan__features">
                        {coreFeatures.map((f) => (
                            <li key={f}><CheckOutlined /> {f}</li>
                        ))}
                        <li><CheckOutlined /> <strong>Unlimited subcontractor accounts</strong> — no extra cost</li>
                        <li><CheckOutlined /> 50 active users included (internal + sub-connected combined)</li>
                        <li><CheckOutlined /> Priority email support</li>
                    </ul>
                    <div className="pl-plan__overage">
                        <strong>How the 50-user cap works</strong>
                        <p>
                            Subs are <strong>free and unlimited</strong> on the Project plan. The cap counts
                            <strong> active users</strong> — anyone (your team or sub-connected) added to
                            this project or listed as a user on it. Login activity doesn&apos;t matter; if
                            they&apos;re in the project, they count. Above 50? Each additional user is
                            <strong> $39/user/mo CAD</strong>.
                        </p>
                    </div>
                </div>

                {/* BY USER */}
                <div className="pl-plan">
                    <span className="pl-plan__ribbon pl-plan__ribbon--muted">Variable · flex monthly</span>
                    <div className="pl-plan__head">
                        <span className="pl-plan__tier">Per User</span>
                        <h2>By User</h2>
                        <p className="pl-plan__desc">
                            Per-seat pricing that <strong>flexes with your team</strong>. Add or drop
                            users month to month, run unlimited projects on one license — perfect for
                            growing teams and seasonal headcounts.
                        </p>
                        <div className="pl-plan__price">
                            <span className="pl-plan__price-num">$39</span>
                            <span className="pl-plan__price-suffix">CAD / user / month</span>
                        </div>
                    </div>
                    <Link to="/signup?plan=user">
                        <Button type="default" block size="large" className="pl-plan__cta pl-plan__cta--ghost">
                            Start 90-day trial
                        </Button>
                    </Link>
                    <p className="pl-plan__note">Scale users up or down monthly</p>
                    <ul className="pl-plan__features">
                        {coreFeatures.map((f) => (
                            <li key={f}><CheckOutlined /> {f}</li>
                        ))}
                        <li><CheckOutlined /> <strong>Unlimited active projects</strong></li>
                        <li><CheckOutlined /> <strong>Subcontractor accounts free to add</strong></li>
                        <li><CheckOutlined /> Priority email support</li>
                    </ul>
                    <div className="pl-plan__overage">
                        <strong>How $39/user/mo works</strong>
                        <p>
                            <strong>Every user counts</strong> — anyone added to a project or listed as
                            a user, on your company or a subcontractor company, billed at $39/mo. Login
                            activity doesn&apos;t matter; if they&apos;re in the system, they&apos;re a
                            user. Sub accounts themselves are free to set up.
                        </p>
                    </div>
                </div>
            </div>

            {/* Cost-savings call-to-action banner under the plans */}
            <div className="pl-pricing-savings-banner">
                <div className="pl-pricing-savings-banner__icon" aria-hidden="true">
                    <span>$</span>
                </div>
                <div>
                    <strong>The math is simple: flat beats per-seat for almost every active project.</strong>
                    <p>
                        Five people on a job? You&apos;re probably fine on the User plan. The moment you cross
                        ~16 active users — including subs — <strong>By Project</strong> is the better
                        deal, and it stays the better deal forever. No surprise bills, no &ldquo;you grew, you
                        owe&rdquo; emails. Just one line item per job.
                    </p>
                </div>
            </div>

            {/* ── Live calculator ────────────────────────────── */}
            <PricingCalculator />

            {/* ── Which plan, when? ──────────────────────────── */}
            <WhichPlanWhen />

            {/* ── Why this pricing works ─────────────────────── */}
            <WhyPricingWorks />

            {/* ── Add-ons ────────────────────────────────────────── */}
            <div className="pl-pricing-subs">
                <div className="pl-pricing-subs__lead">
                    <h3>How subcontractors are billed</h3>
                    <p>Two simple rules — pick the plan that matches your team.</p>
                </div>

                <div className="pl-pricing-subs__grid">
                    {/* By Project — winner card */}
                    <div className="pl-subs-card pl-subs-card--featured">
                        <div className="pl-subs-card__header">
                            <span className="pl-subs-card__badge">Recommended</span>
                            <h4>By Project plan</h4>
                            <p>One flat fee per project. Bring your whole sub roster.</p>
                        </div>
                        <ul className="pl-subs-card__rules">
                            <li>
                                <span className="pl-subs-card__label">Subcontractor accounts</span>
                                <span className="pl-subs-card__value pl-subs-card__value--free">
                                    Unlimited · Free
                                </span>
                            </li>
                            <li>
                                <span className="pl-subs-card__label">Active users included</span>
                                <span className="pl-subs-card__value">50 per project</span>
                            </li>
                            <li>
                                <span className="pl-subs-card__label">Above 50 active users</span>
                                <span className="pl-subs-card__value">$39 / user / mo CAD</span>
                            </li>
                        </ul>
                        <div className="pl-subs-card__footnote">
                            <strong>Active users</strong> = your team + any sub-connected users actually
                            assigned to this project. <strong>Sub accounts themselves don&apos;t count
                            unless they&apos;re working on the project.</strong>
                        </div>
                    </div>

                    {/* By User card */}
                    <div className="pl-subs-card">
                        <div className="pl-subs-card__header">
                            <h4>By User plan</h4>
                            <p>Subs are free to add. You pay per active user.</p>
                        </div>
                        <ul className="pl-subs-card__rules">
                            <li>
                                <span className="pl-subs-card__label">Subcontractor accounts</span>
                                <span className="pl-subs-card__value pl-subs-card__value--free">
                                    Unlimited · Free
                                </span>
                            </li>
                            <li>
                                <span className="pl-subs-card__label">Per active user</span>
                                <span className="pl-subs-card__value">$39 / user / mo CAD</span>
                            </li>
                            <li>
                                <span className="pl-subs-card__label">Who counts as a user</span>
                                <span className="pl-subs-card__value">Anyone added to a project or listed in the system</span>
                            </li>
                        </ul>
                        <div className="pl-subs-card__footnote">
                            <strong>A &ldquo;user&rdquo; is anyone added to your account</strong> — whether
                            they&apos;re on <strong>your company</strong> or a <strong>subcontractor
                            company</strong>, attached to a project, or listed as a user. Login activity
                            doesn&apos;t matter — if they&apos;re in the system, they count. Sub
                            <em> accounts</em> are free to set up; sub <em>users</em> are billed the same
                            as your internal team.
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Why we cost less ───────────────────────────────── */}
            <div className="pl-pricing-why">
                <p className="pl-pricing-why__eyebrow">Why we cost less</p>
                <h2>We&apos;re not a sales-driven enterprise — we&apos;re builders.</h2>
                <div className="pl-pricing-why__grid">
                    <div>
                        <h4>No bloated sales team</h4>
                        <p>
                            Procore and the legacy giants spend an enormous chunk of every dollar on
                            sales reps, conferences, and seat license enforcement. We don&apos;t. You see the
                            price, you click sign up, you&apos;re building. The savings go straight to you.
                        </p>
                    </div>
                    <div>
                        <h4>Built for Canadian builders, by Canadian builders</h4>
                        <p>
                            ProjectLoop was designed by people who actually managed Canadian job sites — from
                            Ontario condos to Alberta industrial builds. CAD pricing, Canadian privacy and
                            data-residency requirements, and a platform that runs offline at the slab edge.
                            Local market. Local support. Local sensibility.
                        </p>
                    </div>
                    <div>
                        <h4>Streamlined operations = streamlined pricing</h4>
                        <p>
                            Our motto: keep the business lean so the client gets the best price.
                            Self-serve onboarding, public pricing, no annual lock-in, and pricing that
                            scales the way <em>your</em> business actually scales — not ours.
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Procore comparison ─────────────────────────────── */}
            <div className="pl-pricing-compare">
                <p className="pl-pricing-compare__eyebrow">The big difference</p>
                <h2>Procore bills you a percentage of your project value. We don&apos;t.</h2>
                <p className="pl-pricing-compare__sub">
                    Procore&apos;s pricing scales with your <strong>Annual Construction Volume</strong> —
                    typically 1% to 3% of every dollar of work you do. Build a bigger job, pay them more.
                    Win a banner project, write a bigger check. ProjectLoop is a <strong>flat $599 CAD per
                    project per month.</strong> A $5M build and a $50M build cost the exact same to manage
                    on our platform. Period.
                </p>

                {/* Shock-value cost calculator */}
                <div className="pl-pricing-shock">
                    <div className="pl-pricing-shock__head">
                        <p className="pl-pricing-shock__eyebrow">What you&apos;d actually pay</p>
                        <h3>One year of project-management software, by project value.</h3>
                        <p className="pl-pricing-shock__caption">
                            Industry-reported Procore figures shown at <strong>1.5% of construction
                            volume</strong> — the middle of the 1–3% range publicly cited by GCs. Procore
                            does not publish list pricing.
                            <br />
                            <br />
                            <strong>ProjectLoop figures are approximate</strong> and reflect the Project
                            plan base rate ($599 CAD/mo × 12). <strong>Subcontractors are unlimited and
                            free on the Project plan.</strong> The first 50 active users (your team + any
                            sub-connected users assigned to the project) are included; additional active
                            users above 50 are billed at <strong>$39/user/mo CAD</strong>. Final cost
                            depends on your active-user count.
                        </p>
                    </div>

                    <div className="pl-pricing-shock__grid">
                        <div className="pl-shock-card">
                            <span className="pl-shock-card__size">$5M</span>
                            <span className="pl-shock-card__sublabel">construction project · 12 months</span>
                            <div className="pl-shock-card__row pl-shock-card__row--them">
                                <span>Procore</span>
                                <strong>~$75,000<small>USD</small></strong>
                            </div>
                            <div className="pl-shock-card__row pl-shock-card__row--us">
                                <span>ProjectLoop</span>
                                <strong>~$7,188<small>CAD</small></strong>
                            </div>
                            <div className="pl-shock-card__savings">
                                You keep <strong>~$65K+</strong>
                            </div>
                        </div>

                        <div className="pl-shock-card pl-shock-card--featured">
                            <span className="pl-shock-card__size">$20M</span>
                            <span className="pl-shock-card__sublabel">construction project · 12 months</span>
                            <div className="pl-shock-card__row pl-shock-card__row--them">
                                <span>Procore</span>
                                <strong>~$300,000<small>USD</small></strong>
                            </div>
                            <div className="pl-shock-card__row pl-shock-card__row--us">
                                <span>ProjectLoop</span>
                                <strong>~$7,188<small>CAD</small></strong>
                            </div>
                            <div className="pl-shock-card__savings">
                                You keep <strong>~$290K+</strong>
                            </div>
                        </div>

                        <div className="pl-shock-card">
                            <span className="pl-shock-card__size">$50M</span>
                            <span className="pl-shock-card__sublabel">construction project · 12 months</span>
                            <div className="pl-shock-card__row pl-shock-card__row--them">
                                <span>Procore</span>
                                <strong>~$750,000<small>USD</small></strong>
                            </div>
                            <div className="pl-shock-card__row pl-shock-card__row--us">
                                <span>ProjectLoop</span>
                                <strong>~$7,188<small>CAD</small></strong>
                            </div>
                            <div className="pl-shock-card__savings">
                                You keep <strong>~$740K+</strong>
                            </div>
                        </div>
                    </div>

                    <div className="pl-pricing-shock__pull">
                        <span className="pl-pricing-shock__pull-mark">&ldquo;</span>
                        <p>
                            Win a bigger project? Procore wins right alongside you. <strong>Why is your
                            software vendor a partner in your construction value?</strong> They didn&apos;t
                            pour the concrete. They didn&apos;t hire the trades. They didn&apos;t carry the risk.
                            We charge you for the platform — not for your hard work.
                        </p>
                    </div>
                </div>

                <p className="pl-pricing-compare__sub" style={{ marginTop: '48px' }}>
                    Beyond the volume tax, here&apos;s the rest of the story side-by-side:
                </p>
                <div className="pl-compare-table" role="table">
                    <div className="pl-compare-table__head" role="row">
                        <span role="columnheader">Feature</span>
                        <span role="columnheader" className="pl-compare-table__head--us">
                            <strong>ProjectLoop</strong>
                            <small>Built for Canada · CAD</small>
                        </span>
                        <span role="columnheader" className="pl-compare-table__head--them">
                            <strong>Procore</strong>
                            <small>Industry incumbent</small>
                        </span>
                    </div>
                    {competitorRows.map((row) => (
                        <div
                            key={row.feature}
                            className={`pl-compare-table__row${row.emphasis ? ' pl-compare-table__row--emphasis' : ''}`}
                            role="row"
                        >
                            <span role="cell" className="pl-compare-table__feature">{row.feature}</span>
                            <span role="cell" className="pl-compare-table__cell pl-compare-table__cell--us">
                                {row.projectloop === true ? (
                                    <CheckOutlined className="pl-compare-table__check" />
                                ) : (
                                    row.projectloop
                                )}
                            </span>
                            <span role="cell" className="pl-compare-table__cell pl-compare-table__cell--them">
                                {row.procore === true ? (
                                    <CheckOutlined className="pl-compare-table__check pl-compare-table__check--neutral" />
                                ) : row.procore === false ? (
                                    <CloseOutlined className="pl-compare-table__close" />
                                ) : (
                                    row.procore
                                )}
                            </span>
                        </div>
                    ))}
                </div>
                <p className="pl-pricing-compare__footnote">
                    Procore pricing references based on publicly reported small/mid GC quotes and industry
                    review sites. Procore does not publish list pricing — your quote will vary.
                </p>
            </div>

            {/* ── Closing CTA ────────────────────────────────────── */}
            <div className="pl-pricing-final-cta">
                <div className="pl-pricing-final-cta__card">
                    <p className="pl-pricing-final-cta__eyebrow">No demo required</p>
                    <h2>Try the whole platform free for 90 days.</h2>
                    <p>
                        Run a real project on ProjectLoop with your real crews. If we don&apos;t pay for
                        ourselves before day 90, walk away — no card, no commitment, no problem.
                    </p>
                    <div className="pl-pricing-final-cta__actions">
                        <Link to="/signup">
                            <Button type="primary" size="large" className="pl-plan__cta">
                                Start free trial
                            </Button>
                        </Link>
                        <Link to="/contact_us">
                            <Button size="large" className="pl-plan__cta pl-plan__cta--ghost">
                                Talk to a builder (not a salesperson)
                            </Button>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
